import { useMemo } from 'react';
import AppEnvironment from './environment/app-environment';
import { useAppSelector } from './ducks/hooks';
import { ETabId, TTabConfig } from './services/tab-configurations/tab-types';
import { EQuotaAcknowledgementStatus } from './ducks/api-ducks/participation-details-duck/types-participation-details';

export const preferredDefaultTabOrder = [ETabId.PerformanceTab] as const;

// Show or hide Filters depending on active tab
export const useShowFilters = () => {
  const activeTab = useAppSelector((state) => state.filters?.activeTab);
  return useMemo(() => {
    switch (activeTab) {
      case ETabId.Global:
      case ETabId.ImpersonationTab:
      case ETabId.PlanSetupTab:
      case ETabId.NotificationsAdminTab:
        return false;
      default:
        return true;
    }
  }, [activeTab]);
};

// Set the refresh time for user notifications depending on environment.
// Local 10 seconds, UAT/PPE 1 min, Prod 30 mins, default 100 mins
export const useGetNotifciationRefreshTime = () =>
  useMemo(() => {
    switch (AppEnvironment.env) {
      case 'local':
        return 10000; // 10 seconds
      case 'uat':
      case 'ppe':
      case 'fed-ppe':
        return 60000; // 1 min
      case 'prod':
      case 'fed-prod':
        return 1800000; // 30 mins
      default:
        // all non defined return 100 mins
        return 6000000; // 100 mins
    }
  }, []);

// Determine the default tab independently from the visual tab order.
export const useDefaultTab = (availableTabs: TTabConfig[]) => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const filters = useAppSelector((state) => state.filters);

  return useMemo(() => {
    if (availableTabs.length === 0) {
      return undefined;
    }

    if (participationDetails && filters) {
      const selectedParticipation = participationDetails.coreParticipations.find(
        (participation) =>
          filters.participationId === participation.participationId &&
          filters.selectedParticipationPartitionGroup?.partitionGroup === participation.fiscalYearId.toString()
      );

      // Determine if the selected participation requires compliance tab to be shown
      // Compliance is required if the plan is active but either the revenue release is not enabled or the quota has not been acknowledged.
      const requiresCompliance =
        selectedParticipation?.isPlanActive &&
        (!selectedParticipation.isRevenueReleaseEnabled ||
          selectedParticipation.quotaAcknowledgementStatus !== EQuotaAcknowledgementStatus.QuotaAccepted);

      if (requiresCompliance && availableTabs.some((tabItem) => tabItem.tabId === ETabId.ComplianceTab)) {
        return ETabId.ComplianceTab;
      }
    }

    // If no compliance tab is required or available, fall back to the preferred default tab order.
    const preferredDefaultTab = preferredDefaultTabOrder.find((tabId) => availableTabs.some((tabItem) => tabItem.tabId === tabId));
    if (preferredDefaultTab) {
      return preferredDefaultTab;
    }

    // If no preferred default tab is available, fall back to the first available tab.
    return availableTabs[0]?.tabId;
  }, [availableTabs, participationDetails, filters]);
};
